import React, { useContext, useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { ProductContext } from '../../../store';
import { format, startOfMonth } from "date-fns";
import { PostData } from "../../../services/FetchData";
import moment from "moment";
import { Col, Row, Spinner } from "reactstrap";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import CredentialSkeleton from "../../../components/Skeleton/CredentialSkeleton";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import ReportHeader from "../../../components/Search/ReportHeader";

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend
);
const options = {
	responsive: true,
	plugins: {
		legend: {
			display: false,
		},
	},
	scales: {
		x: {
			grid: {
				display: false,
			},
		},
		y: {
			beginAtZero: true,
		},
	},
};

const conversionOptions = {
	responsive: true,
	plugins: {
		legend: {
			display: true,
			position: 'top',
		},
	},
	scales: {
		x: {
			grid: {
				display: false,
			},
		},
		y: {
			beginAtZero: true,
		},
	},
};

const labels = ["January", "February", "March", "April", "May", "June", "July"];

const data = {
	labels,
	datasets: [
		{
			label: "Search",
			data: [0, 0, 0, 0, 0, 0, 0],
			fill: false,
			borderColor: "#2b4cc5",
			tension: 0.3,
		},
	],
};

const data1 = {
	labels,
	datasets: [
		{
			label: "Total Click",
			data: [0, 0, 0, 0, 0, 0, 0],
			fill: false,
			borderColor: "#d88d49",
			tension: 0.3,
		},
	],
};

const data2 = {
	labels,
	datasets: [
		{
			label: "Total Cost",
			data: [0, 0, 0, 0, 0, 0, 0],
			fill: false,
			borderColor: "#008000",
			tension: 0.3,
		},
	],
};

// Conversion Analytics Graph Data
const conversionRateData = {
	labels,
	datasets: [
		{
			label: "Search Conversion Rate",
			data: [0, 0, 0, 0, 0, 0, 0],
			fill: false,
			borderColor: "#2b4cc5",
			tension: 0.3,
		},
		{
			label: "Collection Conversion Rate",
			data: [0, 0, 0, 0, 0, 0, 0],
			fill: false,
			borderColor: "#d88d49",
			tension: 0.3,
		},
		{
			label: "Other Conversion Rate",
			data: [0, 0, 0, 0, 0, 0, 0],
			fill: false,
			borderColor: "#008000",
			tension: 0.3,
		},
	],
};

const revenueData = {
	labels,
	datasets: [
		{
			label: "Search Revenue",
			data: [0, 0, 0, 0, 0, 0, 0],
			fill: false,
			borderColor: "#2b4cc5",
			tension: 0.3,
		},
		{
			label: "Collection Revenue",
			data: [0, 0, 0, 0, 0, 0, 0],
			fill: false,
			borderColor: "#d88d49",
			tension: 0.3,
		},
		{
			label: "Other Revenue",
			data: [0, 0, 0, 0, 0, 0, 0],
			fill: false,
			borderColor: "#008000",
			tension: 0.3,
		},
	],
};

const conversionsData = {
	labels,
	datasets: [
		{
			label: "Search Conversions",
			data: [0, 0, 0, 0, 0, 0, 0],
			fill: false,
			borderColor: "#2b4cc5",
			tension: 0.3,
		},
		{
			label: "Collection Conversions",
			data: [0, 0, 0, 0, 0, 0, 0],
			fill: false,
			borderColor: "#d88d49",
			tension: 0.3,
		},
		{
			label: "Other Conversions",
			data: [0, 0, 0, 0, 0, 0, 0],
			fill: false,
			borderColor: "#008000",
			tension: 0.3,
		},
	],
};

const Dashboard = () => {
	const [activeItem, setActiveItem] = useState("Dashboard");
	const [activeTab, setActiveTab] = useState(1), // Default to "Search Phrase"
		[activeGraph, setActiveGraph] = useState("search"),
		[analyticsData, setAnalyticsData] = useState({}),
		[totalSearch, setTotalSearch]	= useState(0),
		[totalClicked, setTotalClicked]	= useState(0),
		[totalCollectionViewed, setTotalCollectionViewed]	= useState(0),
		[usageLoader, setUsageLoader] = useState(true),
		[conversionLoader, setConversionLoader] = useState(true),
		[popularSearch, setPopularSearch] = useState([]),
		[searchGraph, setSearchGraph] = useState(null),
		[clickGraph, setClickGraph] = useState(null),
		[priceGraph, setPriceGraph] = useState(null),
		[noItemFound, setNoItemFound] = useState([]),
		[productClicked, setProductClicked] = useState([]),
		[popularResult, setPopularResult] = useState([]),
		[graphData, setGraphData] = useState(data),
		// Conversion Analytics State
		[conversionRateGraph, setConversionRateGraph] = useState(conversionRateData),
		[revenueGraph, setRevenueGraph] = useState(revenueData),
		[conversionsGraph, setConversionsGraph] = useState(conversionsData),
		[activeConversionGraph, setActiveConversionGraph] = useState("conversionRate"),
		[conversionGraphData, setConversionGraphData] = useState(conversionRateData),
		// Total Conversion Analytics Data
		[totalConversionRate, setTotalConversionRate] = useState(0),
		[totalRevenue, setTotalRevenue] = useState(0),
		{
			currentWebsiteId,
			dateRange,
			setDateRange
		} = useContext(ProductContext),
		[totalConversions, setTotalConversions] = useState(0);

	useEffect(() => {
		if(currentWebsiteId) {
			getWebsiteAnalytics();
			getConversionAnalytics();
		}
	}, [dateRange, currentWebsiteId]);

	const getWebsiteAnalytics = async () => {

		let websiteId	= currentWebsiteId,
			postData	= {
							websiteId,
							startDate: format(dateRange.startDate, "yyyy-MM-dd"),
							endDate: format(dateRange.endDate, "yyyy-MM-dd")
						}
		setUsageLoader(true)
		PostData("ms1", "api/v1/analytics/get", postData).then(response => {
			if( response !== "Invalid" && response !== undefined ){
				if( response.status === "success" ){
					setTotalSearch(response.data.totalSearch);
					setTotalClicked(response.data.totalClicked)
					setTotalCollectionViewed(response.data.totalCollectionViewed)
					setPopularSearch(response.data.popularSearchKeyData);
					setNoItemFound(response.data.popularSearchNoResultData);
					setPopularResult(response.data.popularSearchProductData);
					setProductClicked(response.data.clickedProductData)
					const graphDataValue = response.data.dateWiseSearchRecord,
						clickGraphDataValue = response.data.dateWiseClickRecord,
						priceGraphDataValue = response.data.dateWisePriceRecord,
						collectionViewedGraphDataValue = response.data.dateWiseCollectionViewedRecord,
						graphDataObject = {},
						clickGraphDataObject = {},
						priceGraphDataObject = {},
						collectionViewedGraphDataObject = {},
						previousSevenDays = {},
						previousDataRecord = [],
						previousDataClickRecord = [],
						previousDataPriceRecord = [],
						previousDataCollectionViewedRecord = [];
					for (let i = 0; i < graphDataValue.length; i++) {
						let currentGraphDataValue = graphDataValue[i];
						graphDataObject[currentGraphDataValue._id] = currentGraphDataValue.count;
					}
					for (let i = 0; i < clickGraphDataValue.length; i++) {
						let currentGraphDataValue = clickGraphDataValue[i];
						clickGraphDataObject[currentGraphDataValue._id] = currentGraphDataValue.count;
					}
					for (let i = 0; i < priceGraphDataValue.length; i++) {
						let currentGraphDataValue = priceGraphDataValue[i];
						priceGraphDataObject[currentGraphDataValue._id] = currentGraphDataValue.total_price;
					}
					for (let i = 0; i < collectionViewedGraphDataValue.length; i++) {
						let currentGraphDataValue = collectionViewedGraphDataValue[i];
						collectionViewedGraphDataObject[currentGraphDataValue.date] = currentGraphDataValue.count;
					}
					let currentDate = new Date(dateRange.startDate);
					while (currentDate <= new Date(dateRange.endDate)) {
						const key = moment(currentDate).clone().format("YYYY-MM-DD"),
							value = moment(currentDate).clone().format("MMM DD");
						previousSevenDays[key] = value;
						if (typeof graphDataObject[key] !== "undefined") {
							previousDataRecord.push(graphDataObject[key]);
						} else {
							previousDataRecord.push(0);
						}
						if (typeof clickGraphDataObject[key] !== "undefined") {
							previousDataClickRecord.push(clickGraphDataObject[key]);
						} else {
							previousDataClickRecord.push(0);
						}
						if (typeof priceGraphDataObject[key] !== "undefined") {
							previousDataPriceRecord.push(priceGraphDataObject[key]);
						} else {
							previousDataPriceRecord.push(0);
						}
						if (typeof collectionViewedGraphDataObject[key] !== "undefined") {
							previousDataCollectionViewedRecord.push(collectionViewedGraphDataObject[key]);
						} else {
							previousDataCollectionViewedRecord.push(0);
						}
						currentDate.setDate(currentDate.getDate() + 1);
					}

					data1.labels = Object.values(previousSevenDays);
					data1.datasets[0].data = previousDataClickRecord
					setClickGraph({ ...data1 })
					data2.labels = Object.values(previousSevenDays);
					data2.datasets[0].data = previousDataCollectionViewedRecord
					setPriceGraph({ ...data2 })
					data.labels = Object.values(previousSevenDays);
					data.datasets[0].data = previousDataRecord;
					setActiveGraph("search")
					setGraphData({ ...data });
					setSearchGraph({ ...data })
				}else if( response.status === "failure" ){
					toast.error(response.message)
				}else{
					toast.error(response.message.message)
				}
			}
			setUsageLoader(false)
		})
	};

	const getConversionAnalytics = async () => {

		let websiteId	= currentWebsiteId,
			postData	= {
							websiteId: parseInt(websiteId),
							startDate: format(dateRange.startDate, "yyyy-MM-dd"),
							endDate: format(dateRange.endDate, "yyyy-MM-dd")
						}
		setConversionLoader(true)
		PostData("ms1", "api/v1/analytics/getDailyConversionReport", postData).then(response => {
			if( response !== "Invalid" && response !== undefined ){
				if( response.status === "success" ){
					const dailyData = response.data.dailyData;
					const previousSevenDays = {};
					const searchConversionRateData = [];
					const collectionConversionRateData = [];
					const otherConversionRateData = [];
					const searchRevenueData = [];
					const collectionRevenueData = [];
					const otherRevenueData = [];
					const searchConversionsData = [];
					const collectionConversionsData = [];
					const otherConversionsData = [];
					
					// Calculate totals
					let totalSearchConversionRate = 0;
					let totalCollectionConversionRate = 0;
					let totalOtherConversionRate = 0;
					let totalSearchRevenue = 0;
					let totalCollectionRevenue = 0;
					let totalOtherRevenue = 0;
					let totalSearchConversions = 0;
					let totalCollectionConversions = 0;
					let totalOtherConversions = 0;
					let dayCount = 0;
					
					let currentDate = new Date(dateRange.startDate);
					while (currentDate <= new Date(dateRange.endDate)) {
						const key = moment(currentDate).clone().format("YYYY-MM-DD");
						const value = moment(currentDate).clone().format("MMM DD");
						previousSevenDays[key] = value;
						
						// Find data for this date
						const dayData = dailyData.find(item => item.date === key);
						
						if (dayData) {
							// Conversion Rate Data
							const searchRate = dayData.search.conversion_rate || 0;
							const collectionRate = dayData.collection.conversion_rate || 0;
							const otherRate = dayData.other.conversion_rate || 0;
							
							searchConversionRateData.push(searchRate);
							collectionConversionRateData.push(collectionRate);
							otherConversionRateData.push(otherRate);
							
							// Revenue Data
							const searchRev = dayData.search.revenue || 0;
							const collectionRev = dayData.collection.revenue || 0;
							const otherRev = dayData.other.revenue || 0;
							
							searchRevenueData.push(searchRev);
							collectionRevenueData.push(collectionRev);
							otherRevenueData.push(otherRev);
							
							// Conversions Data
							const searchConv = dayData.search.conversions || 0;
							const collectionConv = dayData.collection.conversions || 0;
							const otherConv = dayData.other.conversions || 0;
							
							searchConversionsData.push(searchConv);
							collectionConversionsData.push(collectionConv);
							otherConversionsData.push(otherConv);
							
							// Accumulate totals
							totalSearchConversionRate += searchRate;
							totalCollectionConversionRate += collectionRate;
							totalOtherConversionRate += otherRate;
							totalSearchRevenue += searchRev;
							totalCollectionRevenue += collectionRev;
							totalOtherRevenue += otherRev;
							totalSearchConversions += searchConv;
							totalCollectionConversions += collectionConv;
							totalOtherConversions += otherConv;
							dayCount++;
						} else {
							// No data for this date, push zeros
							searchConversionRateData.push(0);
							collectionConversionRateData.push(0);
							otherConversionRateData.push(0);
							searchRevenueData.push(0);
							collectionRevenueData.push(0);
							otherRevenueData.push(0);
							searchConversionsData.push(0);
							collectionConversionsData.push(0);
							otherConversionsData.push(0);
						}
						
						currentDate.setDate(currentDate.getDate() + 1);
					}
					
					// Calculate averages for conversion rate
					const avgConversionRate = dayCount > 0 ? 
						((totalSearchConversionRate + totalCollectionConversionRate + totalOtherConversionRate) / dayCount).toFixed(2) : 0;
					
					// Set total values
					setTotalConversionRate(avgConversionRate);
					setTotalRevenue((totalSearchRevenue + totalCollectionRevenue + totalOtherRevenue).toFixed(2));
					setTotalConversions(totalSearchConversions + totalCollectionConversions + totalOtherConversions);
					
					// Update Conversion Rate Graph
					const updatedConversionRateData = { ...conversionRateData };
					updatedConversionRateData.labels = Object.values(previousSevenDays);
					updatedConversionRateData.datasets[0].data = searchConversionRateData;
					updatedConversionRateData.datasets[1].data = collectionConversionRateData;
					updatedConversionRateData.datasets[2].data = otherConversionRateData;
					setConversionRateGraph(updatedConversionRateData);
					
					// Update Revenue Graph
					const updatedRevenueData = { ...revenueData };
					updatedRevenueData.labels = Object.values(previousSevenDays);
					updatedRevenueData.datasets[0].data = searchRevenueData;
					updatedRevenueData.datasets[1].data = collectionRevenueData;
					updatedRevenueData.datasets[2].data = otherRevenueData;
					setRevenueGraph(updatedRevenueData);
					
					// Update Conversions Graph
					const updatedConversionsData = { ...conversionsData };
					updatedConversionsData.labels = Object.values(previousSevenDays);
					updatedConversionsData.datasets[0].data = searchConversionsData;
					updatedConversionsData.datasets[1].data = collectionConversionsData;
					updatedConversionsData.datasets[2].data = otherConversionsData;
					setConversionsGraph(updatedConversionsData);
					
					// Set default active conversion graph
					setActiveConversionGraph("conversionRate");
					setConversionGraphData(updatedConversionRateData);
					
				}else if( response.status === "failure" ){
					toast.error(response.message)
				}else{
					toast.error(response.message.message)
				}
			}
			setConversionLoader(false)
		})
	};

	return (
		<React.Fragment>
			<div>
				<ReportHeader range={dateRange} onDateChange={(range) => setDateRange(range)} />
				{/* Conversion Analytics Section */}
				<div className="row mt-5">
					<Col xs="12">
						<h3>Conversion Analytics</h3>
					</Col>
				</div>
				
				{/* Conversion Analytics Graph Controls */}
				<Row className="mt-3 justify-content-between">
					<div className="row mt-3">
						<div style={{display: "flex",flexDirection: "row",justifyContent: "start",gap: "16px",flexWrap: "nowrap",width: "100%",}}>
							<div className={`boxOne search ${activeConversionGraph === "conversionRate" ? "activeItem" : ""}`} style={{boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",cursor: "pointer",borderTopColor: "#2b4cc5",}} onClick={() => {setConversionGraphData(conversionRateGraph);setActiveConversionGraph("conversionRate");}}>
								<div className="top-row">
									<span>Conversion Rate</span>
									<i className="fa fa-percentage" aria-hidden="true" style={{ marginLeft: "8px" }} />
								</div>
								{conversionLoader ? (
									<div>
										<Spinner style={{ width: "20px", height: "20px" }} />{" "}
									</div>
								) : (
									<b>{totalConversionRate}%</b>
								)}
							</div>
							<div className={`boxOne click ${activeConversionGraph === "revenue" ? "activeItem" : "" }`} style={{boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",cursor: "pointer", borderTopColor: "#d88d49",}} onClick={() => { setConversionGraphData(revenueGraph);setActiveConversionGraph("revenue");}}>
								<div className="top-row">
									<span>Revenue</span>
									<i className="fa fa-dollar" aria-hidden="true" style={{ marginLeft: "8px" }} />
								</div>
								{conversionLoader ? (
									<div>
										<Spinner style={{ width: "20px", height: "20px" }} />{" "}
									</div>
								) : (
									<b>${totalRevenue}</b>
								)}
							</div>
							<div className={`boxTwo price ${activeConversionGraph === "conversions" ? "activeItem" : "" }`} style={{boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",cursor: "pointer",borderTopColor: "#008000",}} onClick={() => {setConversionGraphData(conversionsGraph);setActiveConversionGraph("conversions");}}>
								<div className="top-row">
									<span>Conversions</span>
									<i className="fa fa-shopping-cart" aria-hidden="true" style={{ marginLeft: "8px" }} />
								</div>
								{conversionLoader ? (
									<div>
										<Spinner style={{ width: "20px", height: "20px" }} />{" "}
									</div>
								) : (
									<b>{totalConversions}</b>
								)}
							</div>
						</div>
					</div>
				</Row>
				{/* Conversion Analytics Graph */}
				<div className="row mt-5">
					{conversionLoader ? (
						<CredentialSkeleton height={40} />
					) : (
						<>
							<Line data={conversionGraphData} options={conversionOptions} />
						</>
					)}
				</div>
				<Row className="mt-3 justify-content-between">
					<div className="row mt-3">
						<div style={{display: "flex",flexDirection: "row",justifyContent: "start",gap: "16px",flexWrap: "nowrap",width: "100%",}}>
							<div className={`boxOne search ${activeGraph === "search" ? "activeItem" : ""}`} style={{boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",cursor: "pointer",borderTopColor: "#2b4cc5",}} onClick={() => {setGraphData(searchGraph);setActiveGraph("search");}}>
								<div className="top-row">
									<span>Total searches</span>
									<i className="fa fa-search" aria-hidden="true" style={{ marginLeft: "8px" }} />
								</div>
								{usageLoader ? (
									<div>
										<Spinner style={{ width: "20px", height: "20px" }} />{" "}
									</div>
								) : (
									<b>{totalSearch}</b>
								)}
							</div>
							<div className={`boxOne click ${activeGraph === "click" ? "activeItem" : "" }`} style={{boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",cursor: "pointer", borderTopColor: "#d88d49",}} onClick={() => { setGraphData(clickGraph);setActiveGraph("click");}}>
								<div className="top-row">
									<span>Total Clicks</span>
								</div>
								{usageLoader ? (
									<div>
										<Spinner style={{ width: "20px", height: "20px" }} />{" "}
									</div>
								) : (
									<b>{totalClicked}</b>
								)}
							</div>
							<div className={`boxTwo price ${activeGraph === "price" ? "activeItem" : "" }`} style={{boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",cursor: "pointer",borderTopColor: "#008000",}} onClick={() => {setGraphData(priceGraph);setActiveGraph("price");}}>
								<div className="top-row">
									<span>Total collection viewed</span>
								</div>
								{usageLoader ? (
									<div>
										<Spinner style={{ width: "20px", height: "20px" }} />{" "}
									</div>
								) : (
									<b>{totalCollectionViewed}</b>
								)}
							</div>
						</div>
					</div>
				</Row>
				<div className="row mt-5">
					{usageLoader ? (
						<CredentialSkeleton height={40} />
					) : (
						<>
							{/* <Bar options={options} data={graphData} /> */}
							<Line data={graphData} options={options} />
						</>
					)}
				</div>
				<div className="allTableContainer mt-5">
					<div className="row">
						{/* Popular Searches Section */}
						<div className="analyticsStack col-6">
							<h4 style={{ marginBottom: "12px" }}>Popular Searches</h4>
							{!usageLoader && productClicked.length === 0 && (
								<div className="noDataStatsText">No data yet</div>
							)}
							<table className="analyticsTable">
								<thead>
									<tr>
										<td className="analyticsTableCell">Search Term</td>
										<td className="analyticsTableCell">Search Count</td>
									</tr>
								</thead>
								<tbody>
									{Array.from({ length: 5 }, (_, index) => index).map(
										(item, index) => {
											return (
												<tr className="analyticsTableRow" key={index}>
													<td className="analyticsTableCell">
														{usageLoader ? (
															<Skeleton height={20} />
														) : typeof popularSearch[index] !==
															"undefined" ? (
															popularSearch[index]._id
														) : (
															<>&nbsp;</>
														)}
													</td>
													<td className="analyticsTableCell">
														{usageLoader ? (
															<Skeleton height={20} />
														) : typeof popularSearch[index] !==
															"undefined" ? (
															popularSearch[index].count
														) : (
															""
														)}
													</td>
												</tr>
											);
										}
									)}
									{!usageLoader &&
										<tr>
											<td colSpan="2" className="analyticsTableCell" style={{textAlign: "left", fontWeight: "bolder", color: "#2b4cc5", fontSize: "14px",}}>
												<Link to="/search/reports/searchTerms" className="viewAllLink">View All</Link>
											</td>
										</tr>
									}
								</tbody>
							</table>
						</div>

						{/* No Items Found Section */}
						<div className="analyticsStack col-6">
							<h4 style={{ marginBottom: "12px" }}>No Items Found</h4>
							{!usageLoader && noItemFound.length === 0 && (
								<div className="noDataStatsText">No data yet</div>
							)}
							<table className="analyticsTable">
								<thead>
									<tr>
										<td className="analyticsTableCell">Search Term</td>
										<td className="analyticsTableCell">Frequency</td>
									</tr>
								</thead>
								<tbody>
									{Array.from({ length: 5 }, (_, index) => index).map(
										(item, index) => {
											return (
												<tr className="analyticsTableRow" key={index}>
													<td className="analyticsTableCell">
														{usageLoader ? (
															<Skeleton height={20} />
														) : typeof noItemFound[index] !==
															"undefined" ? (
															noItemFound[index]._id
														) : (
															<>&nbsp;</>
														)}
													</td>
													<td className="analyticsTableCell">
														{usageLoader ? (
															<Skeleton height={20} />
														) : typeof noItemFound[index] !==
															"undefined" ? (
															noItemFound[index].count
														) : (
															""
														)}
													</td>
												</tr>
											);
										}
									)}
									{!usageLoader &&
										<tr>
											<td colSpan="2" className="analyticsTableCell" style={{textAlign: "left", fontWeight: "bolder", color: "#2b4cc5", fontSize: "14px",}}>
												<Link to="/search/reports/noResults" className="viewAllLink">View All</Link>
											</td>
										</tr>
									}
								</tbody>
							</table>
						</div>
					</div>

					<div className="row mt-5">
						{/* Popular Products Section */}
						<div className="analyticsStack col-6">
							<h4 style={{ marginBottom: "12px" }}>Popular Products</h4>
							{!usageLoader && productClicked.length === 0 && (
								<div className="noDataStatsText">No data yet</div>
							)}
							<table className="analyticsTable">
								<thead>
									<tr>
										<td className="analyticsTableCell">Product Name</td>
										<td className="analyticsTableCell">View Count</td>
									</tr>
								</thead>
								<tbody>
									{Array.from({ length: 5 }, (_, index) => index).map(
										(item, index) => {
											return (
												<tr className="analyticsTableRow" key={index}>
													<td className="analyticsTableCell">
														{usageLoader ? (
															<Skeleton height={10} />
														) : typeof popularResult[index] !==
															"undefined" ? (
															<div
																style={{
																	display: "flex",
																	alignItems: "center",
																}}
															>
																<img
																	src={popularResult[index].image}
																	alt={popularResult[index].name}
																	width="35"
																	height="35"
																	style={{
																		objectFit: "cover",
																		marginRight: "8px",
																	}}
																/>
																<span>{popularResult[index].name}</span>
															</div>
														) : (
															<>&nbsp;</>
														)}
													</td>
													<td className="analyticsTableCell">
														{usageLoader ? (
															<Skeleton height={10} />
														) : typeof popularResult[index] !==
															"undefined" ? (
															popularResult[index].count
														) : (
															""
														)}
													</td>
												</tr>
											);
										}
									)}
									{!usageLoader &&
										<tr>
											<td colSpan="2" className="analyticsTableCell" style={{textAlign: "left", fontWeight: "bolder", color: "#2b4cc5", fontSize: "14px",}}>
												<Link to="/search/reports/productsShown" className="viewAllLink">View All</Link>
											</td>
										</tr>
									}
								</tbody>
							</table>
						</div>

						{/* Popular Clicks Section */}
						<div className="analyticsStack col-6">
							<h4 style={{ marginBottom: "12px" }}>Product Clicks</h4>
							{!usageLoader && productClicked.length === 0 && (
								<div className="noDataStatsText">No data yet</div>
							)}
							<table className="analyticsTable">
								<thead>
									<tr>
										<td className="analyticsTableCell">Product Name</td>
										<td className="analyticsTableCell">Click Count</td>
									</tr>
								</thead>
								<tbody>
									{Array.from({ length: 5 }, (_, index) => index).map(
										(item, index) => {
											return (
												<tr className="analyticsTableRow" key={index}>
													<td className="analyticsTableCell">
														{usageLoader ? (
															<Skeleton height={10} />
														) : typeof productClicked[index] !==
															"undefined" ? (
															<div
																style={{
																	display: "flex",
																	alignItems: "center",
																}}
															>
																<img
																	src={productClicked[index].image}
																	alt={productClicked[index].name}
																	width="35"
																	height="35"
																	style={{
																		objectFit: "cover",
																		marginRight: "8px",
																	}}
																/>
																<span>{productClicked[index].name}</span>
															</div>
														) : (
															<>&nbsp;</>
														)}
													</td>
													<td className="analyticsTableCell">
														{usageLoader ? (
															<Skeleton height={10} />
														) : typeof productClicked[index] !==
															"undefined" ? (
															productClicked[index].count
														) : (
															""
														)}
													</td>
												</tr>
											);
										}
									)}
									{!usageLoader &&
										<tr>
											<td colSpan="2" className="analyticsTableCell" style={{textAlign: "left", fontWeight: "bolder", color: "#2b4cc5", fontSize: "14px",}}>
												<Link to="/search/reports/productsClicked" className="viewAllLink">View All</Link>
											</td>
										</tr>
									}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Dashboard;

